import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import { Pagination, Mousewheel } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { SwiperRef } from "swiper/react";
import { toJalaali, toGregorian } from "jalaali-js";

type Props = {
  value: string | null;
  onSubmit: (val: string) => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  maxFutureYears?: number;
};

const JALALI_MONTHS = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];

const getDaysInJMonth = (y: number, m: number) =>
  toGregorian(y, m, 1).gd === 31
    ? 31
    : m <= 6
      ? 31
      : m <= 11
        ? 30
        : (y % 33) % 4 === 1
          ? 30
          : 29;

export default function DatePickerContent({
  value,
  onSubmit,
  setOpen,
  maxFutureYears = 0,
}: Props) {
  const now = new Date();
  const baseDateParts = value
    ? value.split("-").map(Number)
    : [now.getFullYear(), now.getMonth() + 1, now.getDate()];
  const [baseYear, baseMonth, baseDay] = baseDateParts;
  const { jy, jm, jd } = toJalaali(baseYear, baseMonth, baseDay);

  const [year, setYear] = useState(jy);
  const [month, setMonth] = useState(jm);
  const [day, setDay] = useState(jd);

  const currentJalaliYear = toJalaali(
    now.getFullYear(),
    now.getMonth() + 1,
    now.getDate(),
  ).jy;
  const maxYear =
    maxFutureYears > 0 ? currentJalaliYear + maxFutureYears : currentJalaliYear;

  const years = Array.from({ length: maxYear - 1300 + 1 }, (_, i) =>
    (1300 + i).toString(),
  );

  const swiperYearRef = useRef<SwiperRef | null>(null);
  const swiperMonthRef = useRef<SwiperRef | null>(null);
  const swiperDayRef = useRef<SwiperRef | null>(null);

  const dayOptions = Array.from(
    { length: getDaysInJMonth(year, month) },
    (_, i) => (i + 1).toString(),
  );
  const activeDayIndex = Math.max(0, Math.min(dayOptions.length - 1, day - 1));

  const scrollToDate = useCallback(() => {
    swiperYearRef.current?.swiper.slideTo(years.indexOf(year.toString()));
    swiperMonthRef.current?.swiper.slideTo(month - 1);
    swiperDayRef.current?.swiper.slideTo(activeDayIndex);
  }, [activeDayIndex, month, year, years]);

  useEffect(() => {
    scrollToDate();
  }, [scrollToDate]);

  const slideClass = "flex items-center justify-center select-none";
  const labelClass = (active: boolean) =>
    clsx(
      "text-base transition-all duration-200",
      active
        ? "text-primary font-semibold opacity-100"
        : "text-default-500 opacity-40",
    );

  return (
    <div className="pt-0 date-picker-slider text-foreground-500 overflow-hidden">
      <div className="relative flex h-36 bg-background rounded-lg p-4">

        {/* Day */}
        <Swiper
          direction="vertical"
          mousewheel
          grabCursor
          modules={[Mousewheel, Pagination]}
          slidesPerView={3}
          centeredSlides
          className="mySwiper h-full flex-1 overflow-hidden"
          onSlideChange={(swiper) => {
            const index = swiper.realIndex;
            if (dayOptions[index]) setDay(parseInt(dayOptions[index]));
          }}
          ref={swiperDayRef}
        >
          {dayOptions.map((d, i) => {
            const numericDay = parseInt(d);
            return (
              <SwiperSlide key={i} className={slideClass}>
                <span className={labelClass(numericDay === day)}>{d}</span>
              </SwiperSlide>
            );
          })}
        </Swiper>
        
        <div className="pointer-events-none absolute inset-x-4 top-1/2 -translate-y-[22px] h-px bg-default-100" />

        {/* Month */}
        <Swiper
          direction="vertical"
          mousewheel
          grabCursor
          modules={[Mousewheel]}
          slidesPerView={3}
          centeredSlides
          className="mySwiper h-full flex-1 overflow-hidden"
          onSlideChange={(swiper) => {
            const newMonth = swiper.realIndex + 1;
            setMonth(newMonth);
            setDay((prev) => Math.min(prev, getDaysInJMonth(year, newMonth)));
          }}
          ref={swiperMonthRef}
        >
          {JALALI_MONTHS.map((m, i) => (
            <SwiperSlide key={i} className={slideClass}>
              <span className={labelClass(month === i + 1)}>{m}</span>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="pointer-events-none absolute inset-x-4 top-1/2 translate-y-[22px] h-px bg-default-100" />

        {/* Year */}
        <Swiper
          direction="vertical"
          mousewheel
          slidesPerView={3}
          centeredSlides
          spaceBetween={0}
          modules={[Mousewheel]}
          className="mySwiper h-full flex-1 overflow-hidden"
          onSlideChange={(swiper) => {
            const newYear = parseInt(years[swiper.realIndex]);
            setYear(newYear);
            setDay((prev) => Math.min(prev, getDaysInJMonth(newYear, month)));
          }}
          ref={swiperYearRef}
        >
          {years.map((y, i) => {
            const numericYear = parseInt(y);
            return (
              <SwiperSlide key={i} className={slideClass}>
                <span className={labelClass(numericYear === year)}>{y}</span>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>

      <div className="flex gap-3 mt-6">
        <button
          type="button"
          className="flex-1 h-11 rounded-lg bg-primary text-white text-sm font-semibold"
          onClick={() => {
            const g = toGregorian(year, month, day);
            const iso = `${g.gy}-${String(g.gm).padStart(2, "0")}-${String(g.gd).padStart(2, "0")}`;
            onSubmit(iso);
            setOpen(false);
          }}
        >
          ثبت کردن
        </button>
        <button
          type="button"
          className="flex-1 h-11 rounded-lg border border-default-100 bg-background text-sm font-semibold text-default-700"
          onClick={() => {
            onSubmit("");
            setOpen(false);
          }}
        >
          پاک کردن
        </button>
      </div>
    </div>
  );
}
